import { Plus, LayoutList, ArrowRight, User, AlertTriangle, Play, CheckCircle2, CalendarClock, XCircle, Users, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionCard } from "@/components/common/SectionCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { TaskCard } from "./TaskCard";
import { TaskModal } from "./TaskModal";
import { TaskDetail } from "./TaskDetail";
import { useTasks } from "@/hooks/useTasks";
import { useUser } from "@/context/UserContext";
import { statusAccent, isOverdue, ukToday, ukAddDays, founderChip } from "@/data/taskOptions";

const COLUMNS = ["To do", "In progress", "Complete"];
const MAX = 6;
const OVERDUE_MAX = 4;
const today = () => ukToday();

const fmtShort = (iso) => { try { return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return iso; } };

const overdueLabel = (t) => {
  const now = new Date();
  if (t.due_date && t.due_time) {
    const diff = now - new Date(`${t.due_date}T${t.due_time}:00`);
    if (diff > 0) {
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `Overdue by ${mins} min`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `Overdue by ${hrs} hour${hrs > 1 ? "s" : ""}`;
      const days = Math.floor(hrs / 24);
      return days >= 7 ? `Overdue since ${fmtShort(t.due_date)}` : `Overdue by ${days} day${days > 1 ? "s" : ""}`;
    }
  }
  if (t.due_date) {
    const days = Math.round((new Date(`${today()}T00:00:00`) - new Date(`${t.due_date}T00:00:00`)) / 86400000);
    if (days >= 7) return `Overdue since ${fmtShort(t.due_date)}`;
    if (days >= 1) return `Overdue by ${days} day${days > 1 ? "s" : ""}`;
  }
  return "Overdue";
};

const sortCol = (arr) =>
  [...arr].sort((a, b) => {
    const ao = a.board_order ?? Infinity, bo = b.board_order ?? Infinity;
    if (ao !== bo) return ao - bo;
    return (a.created_at || "").localeCompare(b.created_at || "");
  });

const OverdueCard = ({ task, canWrite, onOpen, onStart, onComplete, onReschedule, onCancel }) => (
  <div
    data-testid={`overdue-card-${task.id}`}
    onClick={() => onOpen(task)}
    className="group flex cursor-pointer items-center gap-2 rounded-xl border border-[#FB6F5A]/40 bg-white px-3 py-2 transition-all duration-150 hover:-translate-y-0.5 hover:border-[#FB6F5A] hover:shadow-[0_4px_14px_rgba(251,111,90,0.18)]"
  >
    <div className="min-w-0 flex-1">
      <p className="line-clamp-1 text-sm font-semibold leading-snug text-[#14245F]">{task.title}</p>
      <div className="mt-0.5 flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C2410C]"><AlertTriangle size={11} /> {overdueLabel(task)}</span>
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${founderChip(task.assigned_to)}`}>{task.assigned_to}</span>
      </div>
    </div>
    {canWrite && (
      <div className="flex shrink-0 items-center gap-0.5 opacity-75 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
        {task.status === "To do" && (
          <Button size="icon" variant="ghost" data-testid={`overdue-start-${task.id}`} onClick={() => onStart(task)} className="h-7 w-7 rounded-full text-[#06B6D4]" title="Start"><Play size={13} /></Button>
        )}
        <Button size="icon" variant="ghost" data-testid={`overdue-complete-${task.id}`} onClick={() => onComplete(task)} className="h-7 w-7 rounded-full text-[#16A34A]" title="Mark complete"><CheckCircle2 size={13} /></Button>
        <Button size="icon" variant="ghost" data-testid={`overdue-reschedule-${task.id}`} onClick={() => onReschedule(task)} className="h-7 w-7 rounded-full text-[#1D4ED8]" title="Reschedule to tomorrow"><CalendarClock size={13} /></Button>
        <Button size="icon" variant="ghost" data-testid={`overdue-cancel-${task.id}`} onClick={() => onCancel(task)} className="h-7 w-7 rounded-full text-[#9DAAC6]" title="Cancel"><XCircle size={13} /></Button>
      </div>
    )}
  </div>
);

const FounderBoard = ({ founder, accent, T, canWrite, onViewAll, onViewOverdue }) => {
  const d = today();
  const mine = T.tasks.filter((t) => (t.assigned_to === founder || t.assigned_to === "Both") && t.status !== "Cancelled");
  const completedToday = (t) => t.status === "Complete" && (t.completed_at || "").slice(0, 10) === d;
  // surface tasks scheduled today OR whose deadline (due_date) is today, plus tasks completed today
  const relevantToday = (t) => t.task_date === d || t.due_date === d || completedToday(t);
  const todays = mine.filter(relevantToday);

  // TO DO column = tasks due within the next 7 days (today..+7 inclusive), not overdue, with a due date
  const d7 = ukAddDays(7);
  const todoNext7 = mine.filter((t) => t.status === "To do" && !!t.due_date && t.due_date >= d && t.due_date <= d7 && !isOverdue(t));

  // TO DO ordered by due date ascending (today first → +7 last); due_time then board_order/created_at as tiebreaks
  const sortByDue = (arr) =>
    [...arr].sort((a, b) => {
      const dd = (a.due_date || "9999-99-99").localeCompare(b.due_date || "9999-99-99");
      if (dd !== 0) return dd;
      const dt = (a.due_time || "99:99").localeCompare(b.due_time || "99:99");
      if (dt !== 0) return dt;
      const ao = a.board_order ?? Infinity, bo = b.board_order ?? Infinity;
      if (ao !== bo) return ao - bo;
      return (a.created_at || "").localeCompare(b.created_at || "");
    });

  const byStatus = (s) => {
    if (s === "To do") return sortByDue(todoNext7);
    if (s === "Complete") return sortCol(todays.filter(completedToday));
    // IN PROGRESS = all active in-progress tasks for this founder (no due-date / 7-day / today restriction).
    // `mine` already excludes Cancelled + other founder; soft-deleted are excluded upstream; Complete excluded by status.
    return sortCol(mine.filter((t) => t.status === s));
  };

  const summary = {
    total: todays.length,
    overdue: mine.filter((t) => isOverdue(t)).length,
    completed: mine.filter(completedToday).length,
  };

  const overdueTasks = [...mine.filter((t) => isOverdue(t))].sort(
    (a, b) =>
      (a.assigned_to === "Both" ? 0 : 1) - (b.assigned_to === "Both" ? 0 : 1) ||
      (a.due_date || "").localeCompare(b.due_date || "") ||
      (a.due_time || "").localeCompare(b.due_time || "")
  );

  const colOf = (t) => (completedToday(t) ? "Complete" : t.status);
  const applyStatus = (t, col) => T[col === "Complete" ? "complete" : col === "In progress" ? "start" : "reopen"](t);

  const onColDrop = (e, col) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const t = T.tasks.find((x) => x.id === id);
    if (!t || !canWrite) return;
    if (colOf(t) !== col) { applyStatus(t, col); return; }
    const ids = byStatus(col).map((x) => x.id).filter((x) => x !== id);
    ids.push(id);
    T.reorder(ids);
  };

  const onCardDrop = (e, target, col) => {
    e.preventDefault(); e.stopPropagation();
    const id = e.dataTransfer.getData("text/plain");
    const t = T.tasks.find((x) => x.id === id);
    if (!t || !canWrite || id === target.id) return;
    if (colOf(t) !== col) { applyStatus(t, col); return; }
    const ids = byStatus(col).map((x) => x.id).filter((x) => x !== id);
    const idx = ids.indexOf(target.id);
    ids.splice(idx < 0 ? ids.length : idx, 0, id);
    T.reorder(ids);
  };

  const key = founder.toLowerCase();

  return (
    <SectionCard
      testid={`founder-board-${key}`}
      accent={accent}
      icon={User}
      title={`${founder}’s Task Board`}
      subtitle="Today’s workload at a glance"
      action={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid={`board-view-all-${key}`} onClick={onViewAll} className="hidden rounded-full border-[#E4E9F3] text-[#1D4ED8] sm:inline-flex">View all <ArrowRight size={14} /></Button>
          {canWrite && <Button size="sm" data-testid={`board-add-task-${key}`} onClick={() => T.openAddFor(founder)} style={{ backgroundColor: accent }} className="rounded-full text-white hover:opacity-90"><Plus size={15} /> Add task</Button>}
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap gap-2" data-testid={`board-summary-${key}`}>
        <span className="rounded-full bg-[#EEF2FB] px-2.5 py-1 text-xs font-semibold text-[#14245F]" data-testid={`board-total-${key}`}>{summary.total} today</span>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${summary.overdue ? "bg-[#FB6F5A]/12 text-[#C2410C]" : "bg-[#EEF2FB] text-[#5A6B8C]"}`} data-testid={`board-overdue-${key}`}>{summary.overdue} overdue</span>
        <span className="rounded-full bg-[#16A34A]/12 px-2.5 py-1 text-xs font-semibold text-[#15803D]" data-testid={`board-completed-${key}`}>{summary.completed} done today</span>
      </div>

      {overdueTasks.length > 0 && (
        <div data-testid={`board-overdue-section-${key}`} className="mb-3 rounded-2xl border border-[#FB6F5A]/30 bg-[#FB6F5A]/[0.06] p-2.5">
          <div className="mb-2 flex items-center justify-between px-1">
            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#C2410C]">
              <AlertTriangle size={13} /> Overdue
              <span className="rounded-full bg-[#FB6F5A]/15 px-2 py-0.5 text-[10px] font-bold text-[#C2410C]">{overdueTasks.length}</span>
            </span>
            <button data-testid={`board-view-overdue-${key}`} onClick={() => onViewOverdue(founder)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#C2410C] hover:underline">View all overdue <ArrowRight size={12} /></button>
          </div>
          <div className="flex flex-col gap-1.5">
            {overdueTasks.slice(0, OVERDUE_MAX).map((t) => (
              <OverdueCard key={t.id} task={t} canWrite={canWrite}
                onOpen={T.setDetail} onStart={T.start} onComplete={T.complete}
                onReschedule={(x) => T.reschedule(x, ukAddDays(1))} onCancel={T.cancel} />
            ))}
            {overdueTasks.length > OVERDUE_MAX && (
              <button data-testid={`board-overdue-more-${key}`} onClick={() => onViewOverdue(founder)} className="rounded-lg py-1 text-center text-xs font-semibold text-[#C2410C] hover:bg-white">+ {overdueTasks.length - OVERDUE_MAX} more overdue — view all</button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid={`task-board-${key}`}>
        {COLUMNS.map((col) => {
          const items = byStatus(col);
          const colKey = col.replace(/\s/g, "-").toLowerCase();
          return (
            <div
              key={col}
              data-testid={`board-col-${key}-${colKey}`}
              onDragOver={(e) => canWrite && e.preventDefault()}
              onDrop={(e) => onColDrop(e, col)}
              className="flex flex-col rounded-2xl border border-[#E4E9F3] bg-[#F7F9FE]/60 p-2.5"
            >
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: statusAccent(col) }}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: statusAccent(col) }} /> {col}
                  {col === "To do" && <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal text-[#5A6B8C]"><Clock size={9} /> Next 7 days</span>}
                </span>
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-[#5A6B8C]">{items.length}</span>
              </div>
              <div className="flex flex-col gap-2">
                {items.length === 0 ? (
                  <p className="px-1 py-5 text-center text-xs text-[#9DAAC6]">Nothing here.</p>
                ) : (
                  items.slice(0, MAX).map((t) => (
                    <TaskCard key={t.id} task={t} relatedNames={T.relatedNames} canWrite={canWrite} draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", t.id)}
                      onDragOver={(e) => canWrite && e.preventDefault()}
                      onDrop={(e) => onCardDrop(e, t, col)}
                      onOpen={T.setDetail} onStart={T.start} onComplete={T.complete} onReopen={T.reopen} />
                  ))
                )}
                {items.length > MAX && (
                  <button data-testid={`board-more-${key}-${colKey}`} onClick={onViewAll} className="rounded-lg py-1.5 text-center text-xs font-semibold text-[#1D4ED8] hover:bg-white">+ {items.length - MAX} more — view all</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

export const TaskBoard = ({ onViewAll, onViewOverdue }) => {
  const { canWrite } = useUser();
  const T = useTasks();

  return (
    <div className="space-y-5" data-testid="founder-boards">
      <div className="flex items-center justify-between px-1">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-[#14245F]"><LayoutList size={18} className="text-[#06B6D4]" /> Today’s Task Boards</h2>
        <Button size="sm" variant="ghost" data-testid="boards-view-all" onClick={onViewAll} className="rounded-full text-[#1D4ED8]">View all tasks <ArrowRight size={14} /></Button>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <FounderBoard founder="Steven" accent="#1D4ED8" T={T} canWrite={canWrite} onViewAll={onViewAll} onViewOverdue={onViewOverdue} />
        <FounderBoard founder="Richard" accent="#0EA5A4" T={T} canWrite={canWrite} onViewAll={onViewAll} onViewOverdue={onViewOverdue} />
      </div>

      <TaskModal open={T.modalOpen} onOpenChange={T.setModalOpen} initial={T.editing} relations={T.relations} onSubmit={T.save} />
      <TaskDetail task={T.detail} relations={T.relations} relatedNames={T.relatedNames} canWrite={canWrite}
        onOpenChange={(o) => !o && T.setDetail(null)} onEdit={T.openEdit} onStart={T.start} onComplete={T.complete}
        onReopen={T.reopen} onCancel={T.cancel} onDelete={T.setConfirmDelete} onReschedule={T.reschedule} onDuplicate={T.duplicate} onComment={T.comment} onOpenRelated={T.openRelated} />
      <ConfirmDialog open={!!T.confirmDelete} onOpenChange={(o) => !o && T.setConfirmDelete(null)} title="Delete this task?" softDelete onConfirm={(reason) => T.remove(T.confirmDelete, reason)} />
    </div>
  );
};
